import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, useTheme, spacing } from '@fti/design-system';

interface DataGridCellProps {
  value: string | number | null;
  rowIndex: number;
  columnIndex: number;
  isDirty: boolean;
  isDeleted: boolean;
  isActive: boolean;
  isEditing: boolean;
  onActivate: (rowIndex: number, columnIndex: number) => void;
  onStartEdit: (rowIndex: number, columnIndex: number) => void;
  onCommitEdit: (rowIndex: number, columnIndex: number, value: string | number | null) => void;
  onCancelEdit: () => void;
  onNavigate: (direction: 'up' | 'down' | 'left' | 'right') => void;
}

export const DataGridCell: React.FC<DataGridCellProps> = ({
  value,
  rowIndex,
  columnIndex,
  isDirty,
  isDeleted,
  isActive,
  isEditing,
  onActivate,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onNavigate,
}) => {
  const { theme } = useTheme();
  const [editValue, setEditValue] = useState(String(value ?? ''));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(String(value ?? ''));
  }, [value]);

  const handleDoubleClick = useCallback(() => {
    if (!isDeleted) {
      onStartEdit(rowIndex, columnIndex);
    }
  }, [isDeleted, onStartEdit, rowIndex, columnIndex]);

  const handleClick = useCallback(() => {
    if (!isDeleted) {
      onActivate(rowIndex, columnIndex);
    }
  }, [isDeleted, onActivate, rowIndex, columnIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isEditing) {
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const newValue = editValue.trim() === '' ? null : editValue;
          onCommitEdit(rowIndex, columnIndex, newValue);
          if (e.key === 'Tab') {
            onNavigate(e.shiftKey ? 'left' : 'right');
          } else {
            onNavigate('down');
          }
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setEditValue(String(value ?? ''));
          onCancelEdit();
        }
      } else if (isActive) {
        if (e.key === 'ArrowUp') { e.preventDefault(); onNavigate('up'); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); onNavigate('down'); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); onNavigate('left'); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); onNavigate('right'); }
        else if (e.key === 'Enter' || e.key === 'F2') {
          e.preventDefault();
          onStartEdit(rowIndex, columnIndex);
        }
      }
    },
    [isEditing, isActive, editValue, value, rowIndex, columnIndex, onCommitEdit, onCancelEdit, onNavigate, onStartEdit]
  );

  const cellStyle: React.CSSProperties = {
    padding: spacing.md,
    verticalAlign: 'middle',
    maxWidth: '240px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: isDeleted ? 'not-allowed' : 'cell',
    position: 'relative',
    outline: isActive ? `2px solid ${theme.colors.primary[500]}` : 'none',
    outlineOffset: '-2px',
    backgroundColor: isDirty
      ? `${theme.colors.semantic.warning.light}15`
      : isDeleted
        ? `${theme.colors.semantic.error.light}10`
        : undefined,
    textDecoration: isDeleted ? 'line-through' : undefined,
    opacity: isDeleted ? 0.5 : 1,
  };

  if (isEditing) {
    return (
      <td style={{ ...cellStyle, padding: '2px' }}>
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            const newValue = editValue.trim() === '' ? null : editValue;
            onCommitEdit(rowIndex, columnIndex, newValue);
          }}
          size="sm"
          style={{ width: '100%', minWidth: '60px' }}
        />
      </td>
    );
  }

  return (
    <td
      style={cellStyle}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isActive ? 0 : -1}
      role="gridcell"
      aria-colindex={columnIndex + 1}
    >
      {value ?? ''}
    </td>
  );
};
